import { useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { TwoLevelSidebar } from './TwoLevelSidebar';
import { useAuth } from '@/lib/auth';
import { useOrganization } from '@/lib/organization-context';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useAdminCheck } from '@/hooks/useAdminCheck';

import { successToast, errorToast, infoToast } from '@/components/ui/sonner';
import {
  Zap,
  Menu,
  X,
  LayoutDashboard,
  FolderKanban,
  Library,
  BarChart3,
  Sparkles,
  Settings,
  LogOut,
  Play,
  Trash2,
  Sun,
  Moon,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const navItems = [
  { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
  { title: 'Projects', url: '/projects', icon: FolderKanban },
  { title: 'Knowledge Vault', url: '/library', icon: Library },
  { title: 'Analytics', url: '/analytics', icon: BarChart3 },
];

const specialNavItems: typeof navItems = [];

// Mobile drawer content
function MobileNavContent({ onClose }: { onClose: () => void }) {
  const { signOut, user } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const { replayTutorial, developerReset } = useOnboardingState();
  const { isAdmin } = useAdminCheck();
  const { theme, toggleTheme } = useTheme();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleReplayTutorial = async () => {
    try {
      await replayTutorial();
      infoToast('Tutorial ready', 'Loading the onboarding experience...');
    } catch (error) {
      errorToast('Could not replay tutorial', 'Please try again.');
    }
  };

  const handleDeveloperReset = async () => {
    setIsResetting(true);
    try {
      await developerReset();
      successToast('Reset complete', 'All data has been wiped. Redirecting to onboarding...');
      setShowResetDialog(false);
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      console.error('Developer reset failed:', error);
      errorToast('Reset failed', 'There was an error resetting your data. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Logo & Organization */}
        <div className="p-3 border-b border-border/50">

          <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
            <Avatar className="h-7 w-7 rounded-md border border-border/50">
              <AvatarFallback className="rounded-md bg-muted text-xs font-medium">
                {organization?.name?.[0]?.toUpperCase() || 'O'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-medium text-foreground truncate">
                {organization?.name || 'Organization'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.title}
                to={item.url}
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
                activeClassName="bg-muted text-foreground"
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">{item.title}</span>
              </NavLink>
            ))}

            <div className="py-2">
              <Separator className="bg-border/50" />
            </div>

            {specialNavItems.map((item) => (
              <NavLink
                key={item.title}
                to={item.url}
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-primary hover:bg-primary/5"
                activeClassName="bg-primary/10 text-primary"
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">{item.title}</span>
              </NavLink>
            ))}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="mt-auto border-t border-border/50 p-2 space-y-1">
          <NavLink
            to="/settings"
            onClick={handleNavClick}
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
            activeClassName="bg-muted text-foreground"
          >
            <Settings className="w-4 h-4 shrink-0" />
            <span className="whitespace-nowrap">Settings</span>
          </NavLink>

          <Separator className="bg-border/50 my-2" />

          <DropdownMenu>
            <DropdownMenuTrigger className="w-full focus:outline-none">
              <div className="flex items-center gap-2 p-1.5 rounded-md hover:bg-muted/50 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs font-medium bg-muted">
                    {user?.email?.[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-foreground truncate">
                    {user?.user_metadata?.name || user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.user_metadata?.name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { navigate('/settings'); handleNavClick(); }} className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleReplayTutorial} className="cursor-pointer">
                <Play className="w-4 h-4 mr-2" />
                Replay Tutorial
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem 
                  onClick={() => setShowResetDialog(true)} 
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Developer Reset
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <div
                className="flex items-center justify-between px-4 py-2.5 text-[13px] text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTheme(); }}
                role="menuitem"
              >
                <span>Appearance</span>
                <div className="flex items-center">
                  <Sun className="w-4 h-4 text-muted-foreground theme-icon-sun" />
                  <Moon className="w-4 h-4 text-muted-foreground theme-icon-moon" />
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Developer Reset</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will <strong>permanently delete</strong> all your data:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>All proposals and data</li>
                <li>All library content (Q&A, case studies, templates)</li>
                <li>All analytics and simulation results</li>
                <li>All notifications and comments</li>
              </ul>
              <p className="font-medium mt-3">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeveloperReset}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? 'Deleting...' : 'Delete Everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function SideNav() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  // Mobile: Drawer
  if (isMobile) {
    return (
      <>
        <header className="h-14 bg-background border-b border-border flex items-center px-4 sticky top-0 z-50 md:hidden">
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="mr-3">
                <Menu className="w-5 h-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="h-[85vh]">
              <div className="flex justify-end p-2">
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon">
                    <X className="w-4 h-4" />
                  </Button>
                </DrawerClose>
              </div>
              <MobileNavContent onClose={() => setIsDrawerOpen(false)} />
            </DrawerContent>
          </Drawer>
          
          
        </header>
      </>
    );
  }

  // Desktop: Two-level sidebar
  return <TwoLevelSidebar />;
}
